import {
  Fieldset as BaseFieldset,
  type FieldsetLegendProps as BaseFieldsetLegendProps,
  type FieldsetRootProps as BaseFieldsetRootProps,
} from "@base-ui/react/fieldset";
import classNames from "classnames";
import * as React from "react";

import fieldStyles from "../Field/Field.module.css";

import styles from "./Fieldset.module.css";

type PartProps<P> = Omit<P, "className" | "ref"> & { className?: string };

export type FieldsetRootProps = PartProps<BaseFieldsetRootProps>;
export type FieldsetLegendProps = PartProps<BaseFieldsetLegendProps>;
export type FieldsetDescriptionProps = Omit<
  React.HTMLAttributes<HTMLParagraphElement>,
  "id"
>;

/** Lets a description add itself to the fieldset's `aria-describedby`. */
const DescriptionIdContext = React.createContext<
  ((id: string | undefined) => void) | null
>(null);

/** A `<fieldset>` without the browser's border, laid out like `Field.Root`. */
const Root = React.forwardRef<HTMLElement, FieldsetRootProps>(
  function FieldsetRoot({ className, ...props }, ref) {
    const [descriptionId, setDescriptionId] = React.useState<string>();
    return (
      <DescriptionIdContext.Provider value={setDescriptionId}>
        <BaseFieldset.Root
          ref={ref}
          aria-describedby={descriptionId}
          {...props}
          className={classNames(fieldStyles.root, styles.root, className)}
        />
      </DescriptionIdContext.Provider>
    );
  },
);

/** Names the group, styled as a `Field.Label`. */
const Legend = React.forwardRef<HTMLDivElement, FieldsetLegendProps>(
  function FieldsetLegend({ className, ...props }, ref) {
    return (
      <BaseFieldset.Legend
        ref={ref}
        className={classNames(fieldStyles.label, className)}
        {...props}
      />
    );
  },
);

/** Helper text for the group, styled as a `Field.Description`. */
const Description = React.forwardRef<
  HTMLParagraphElement,
  FieldsetDescriptionProps
>(function FieldsetDescription({ className, ...props }, ref) {
  const id = React.useId();
  const setDescriptionId = React.useContext(DescriptionIdContext);
  React.useLayoutEffect(() => {
    setDescriptionId?.(id);
    return () => setDescriptionId?.(undefined);
  }, [id, setDescriptionId]);
  return (
    <p
      ref={ref}
      id={id}
      className={classNames(fieldStyles.description, className)}
      {...props}
    />
  );
});

/**
 * Labels a group of controls that share one name, such as a `ToggleGroup` or
 * a `Slider` with a number box beside it. The legend names the fieldset; give
 * it an `id` when a control inside also needs it as its `aria-labelledby`.
 *
 * @example
 * ```tsx
 * <Fieldset.Root>
 *   <Fieldset.Legend id={legendId}>Image format</Fieldset.Legend>
 *   <ToggleGroup aria-labelledby={legendId} ... />
 *   <Fieldset.Description>PNG keeps every pixel.</Fieldset.Description>
 * </Fieldset.Root>
 * ```
 */
export const Fieldset = Object.assign(Root, { Root, Legend, Description });
